import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import Box from '@mui/material/Box';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import { appLocales } from 'i18n';
import PropTypes from 'prop-types';

import { ReactComponent as OpenPickerIcon } from 'assets/icons/calendar_edit_icon.svg';
import { isInvalidDateRange, validateDate } from 'utils/Date';

const { en, fr } = appLocales;
const localeMap = {
    [en]: enLocale,
    [fr]: frLocale,
};

const theme = createTheme({
    palette: {
        primary: {
            main: '#3f2a56',
        },
    },
    typography: {
        fontFamily: ['Source Sans Pro', 'Helvetica', 'Arial'].join(','),
        htmlFontSize: 10,
    },
});

const DateRangePicker = (props) => {
    const { endDate, startDate } = props;
    const [lastUpdated, setLastUpdated] = useState(null);
    const { locale } = useIntl();

    const isStartDateAfterEndDate = useMemo(() => {
        if (lastUpdated === props.startDateName) {
            return false;
        }

        if (isSameDay(startDate, endDate)) {
            return false;
        }

        return isAfter(startDate, endDate);
    }, [startDate, endDate, lastUpdated]);

    const isEndDateBeforeStartDate = useMemo(() => {
        if (lastUpdated === props.endDateName) {
            return false;
        }

        if (isSameDay(startDate, endDate)) {
            return false;
        }

        return isBefore(endDate, startDate);
    }, [startDate, endDate, lastUpdated]);

    const handleOnChange = (name, dates) => {
        for (const n in dates) {
            const value = dates[n];
            const errorMessage = validateDate(value, props.minDate, props.maxDate);
            dates[n] = {
                errorMessage,
                isValid: !errorMessage,
                value,
            };
        }

        if (
            !dates[props.startDateName].errorMessage &&
            !dates[props.endDateName].errorMessage &&
            isInvalidDateRange(dates[props.startDateName].value, dates[props.endDateName].value)
        ) {
            dates[name].errorMessage = 'invalidDateRange';
        }
        setLastUpdated(name);
        props.onChange(dates, name);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeMap[locale]}>
            <ThemeProvider theme={theme}>
                <div className={props.className}>
                    <DatePicker
                        allowSameDateSelection={props.allowSameDateSelection}
                        cancelText={props.cancelText}
                        components={{ OpenPickerIcon }}
                        disableFuture={props.disableFuture}
                        disableHighlightToday={props.disableHighlightToday}
                        label={props.startDateLabel}
                        maxDate={props.maxDate}
                        minDate={props.minDate}
                        okText={props.okText}
                        onChange={(newDate) =>
                            handleOnChange(props.startDateName, {
                                endDate,
                                startDate: newDate,
                            })
                        }
                        renderInput={({ error, ...params }) => (
                            <TextField
                                {...params}
                                autoComplete="off"
                                error={error || isEndDateBeforeStartDate || !startDate}
                                fullWidth
                            />
                        )}
                        value={startDate}
                        views={['year', 'month', 'day']}
                    />
                    <Box sx={{ m: 1 }}>
                        <FormattedMessage id="date.range.picker.to" />
                    </Box>
                    <DatePicker
                        allowSameDateSelection={props.allowSameDateSelection}
                        cancelText={props.cancelText}
                        components={{ OpenPickerIcon }}
                        disableFuture={props.disableFuture}
                        disableHighlightToday={props.disableHighlightToday}
                        label={props.endDateLabel}
                        maxDate={props.maxDate}
                        minDate={props.minDate}
                        okText={props.okText}
                        onChange={(newDate) =>
                            handleOnChange(props.endDateName, {
                                endDate: newDate,
                                startDate,
                            })
                        }
                        renderInput={({ error, ...params }) => (
                            <TextField
                                {...params}
                                autoComplete="off"
                                error={error || isStartDateAfterEndDate || !endDate}
                                fullWidth
                            />
                        )}
                        value={endDate}
                        views={['year', 'month', 'day']}
                    />
                </div>
            </ThemeProvider>
        </LocalizationProvider>
    );
};

DateRangePicker.propTypes = {
    allowSameDateSelection: PropTypes.bool,
    cancelText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    className: PropTypes.string,
    disableFuture: PropTypes.bool,
    disableHighlightToday: PropTypes.bool,
    endDate: PropTypes.object,
    endDateErrorMessage: PropTypes.string,
    endDateLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    endDateName: PropTypes.string,
    maxDate: PropTypes.object.isRequired,
    minDate: PropTypes.object.isRequired,
    okText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    onChange: PropTypes.func.isRequired,
    startDate: PropTypes.object,
    startDateErrorMessage: PropTypes.string,
    startDateLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    startDateName: PropTypes.string,
};

DateRangePicker.defaultProps = {
    allowSameDateSelection: true,
    cancelText: <FormattedMessage id="date.range.picker.cancelText" />,
    disableFuture: true,
    disableHighlightToday: true,
    endDateLabel: <FormattedMessage id="date.range.picker.end.label" />,
    endDateName: 'endDate',
    okText: <FormattedMessage id="date.range.picker.okText" />,
    startDateLabel: <FormattedMessage id="date.range.picker.start.label" />,
    startDateName: 'startDate',
};

export default DateRangePicker;
